import type { LatLngBounds, Map } from "leaflet";
import {
  getPrintLayout,
  getPxPerMm,
  type MapPrintOptions,
} from "./printLayout";

/**
 * 地図キャプチャ済み Canvas を、最終的な成果物 (画像 / 印刷) に合成する
 * ユーティリティ群。
 *
 * - composeImageCanvas: タイトル + 地図画像 + 任意のスケールバーを合成して
 *   そのまま PNG ダウンロードできる Canvas を返す
 * - composePrintCanvas: A4/A3 用紙レイアウト (タイトル / 地図領域 / フッター)
 *   へ地図キャプチャを焼き込んだ印刷用 Canvas を返す
 * - cropCanvasToBounds: 印刷モードで縮尺指定がある場合に、地図キャプチャを
 *   指定 LatLngBounds の領域へ切り出す
 * - drawScaleBarOnCanvas: Leaflet ScaleControl と同じ見た目のスケールバーを
 *   Canvas に書き足す
 *
 * すべて React / Hook 非依存の純関数として提供する。
 */

/**
 * Leaflet の `.leaflet-control-attribution` から表示中の出典テキストを取り出す。
 *
 * Leaflet の `attributionControl` は内部の `_attributions` を `_update()` で
 * DOM へ反映している。出力では HTML を扱いたくないため、ここでは `textContent`
 * (リンクのアンカーテキストや span のテキストノードのみ) を取り出す。
 * コントロールが存在しない / テキストが空のときは空文字を返す。
 */
export const getMapAttributionText = (mapInstance: Map): string => {
  const container = mapInstance.getContainer();
  const el = container.querySelector(".leaflet-control-attribution");
  return el?.textContent?.trim() ?? "";
};

/**
 * 出典テキストを Canvas の最大幅に収まるように行へ折り返す。
 *
 * Leaflet attribution の表示は " | " 区切りなので、まず区切り単位で改行を試み、
 * それでも 1 トークンが幅を超える場合は文字単位で折り返す (日本語混じり対応)。
 *
 * テスト容易性のため `measure` を関数注入する。
 */
export const wrapAttributionText = (
  text: string,
  maxWidthPx: number,
  measure: (s: string) => number
): string[] => {
  if (!Number.isFinite(maxWidthPx) || maxWidthPx <= 0) return [];
  const tokens = text
    .split(/\s*\|\s*/)
    .map((t) => t.trim())
    .filter(Boolean);
  const lines: string[] = [];
  let current = "";
  for (const token of tokens) {
    const tentative = current ? `${current} | ${token}` : token;
    if (measure(tentative) <= maxWidthPx) {
      current = tentative;
      continue;
    }
    if (current) {
      lines.push(current);
      current = "";
    }
    if (measure(token) <= maxWidthPx) {
      current = token;
      continue;
    }
    // 単独で幅を超えるトークンは文字単位で折り返す
    let chunk = "";
    for (const char of [...token]) {
      const next = chunk + char;
      if (measure(next) <= maxWidthPx) {
        chunk = next;
      } else {
        if (chunk) lines.push(chunk);
        chunk = char;
      }
    }
    if (chunk) current = chunk;
  }
  if (current) lines.push(current);
  return lines;
};

interface AttributionDrawOptions {
  /** 出典を描画する領域 (通常は地図領域) の左上 X */
  mapAreaXPx: number;
  /** 出典を描画する領域の左上 Y */
  mapAreaYPx: number;
  /** 出典を描画する領域の幅 */
  mapAreaWidthPx: number;
  /** 出典を描画する領域の高さ */
  mapAreaHeightPx: number;
  /** フォントサイズ (px) */
  fontSizePx: number;
  /** ラベル内側の左右パディング */
  paddingXPx: number;
  /** ラベル内側の上下パディング */
  paddingYPx: number;
  /** 領域端からのマージン */
  marginPx: number;
}

/**
 * 出力 Canvas の右下に出典 (attribution) ラベルを焼き込む。
 *
 * Leaflet の `.leaflet-control-attribution` と同じく半透明白背景の小さなラベル。
 * 折り返し時は複数行を右寄せで描画する。テキストが空のときは何もしない。
 */
export const drawAttributionOnCanvas = (
  canvas: HTMLCanvasElement,
  text: string,
  options: AttributionDrawOptions
): void => {
  const context = canvas.getContext("2d");
  if (!context) return;
  const trimmed = text.trim();
  if (!trimmed) return;

  const {
    mapAreaXPx,
    mapAreaYPx,
    mapAreaWidthPx,
    mapAreaHeightPx,
    fontSizePx,
    paddingXPx,
    paddingYPx,
    marginPx,
  } = options;

  context.save();
  context.font = `${fontSizePx}px "Helvetica Neue", Arial, Helvetica, sans-serif`;
  context.textBaseline = "top";

  const maxLineWidth = mapAreaWidthPx - marginPx * 2 - paddingXPx * 2;
  const lines = wrapAttributionText(
    trimmed,
    maxLineWidth,
    (s) => context.measureText(s).width
  );
  if (lines.length === 0) {
    context.restore();
    return;
  }

  const lineHeight = fontSizePx * 1.2;
  const widths = lines.map((line) => context.measureText(line).width);
  const maxLineWidthActual = Math.max(...widths);
  const totalTextHeight = lineHeight * lines.length;
  const boxWidth = maxLineWidthActual + paddingXPx * 2;
  const boxHeight = totalTextHeight + paddingYPx * 2;

  const x = mapAreaXPx + mapAreaWidthPx - marginPx - boxWidth;
  const y = mapAreaYPx + mapAreaHeightPx - marginPx - boxHeight;

  context.fillStyle = "rgba(255, 255, 255, 0.75)";
  context.fillRect(x, y, boxWidth, boxHeight);

  context.fillStyle = "#333";
  context.textAlign = "right";
  lines.forEach((line, i) => {
    context.fillText(
      line,
      x + boxWidth - paddingXPx,
      y + paddingYPx + lineHeight * i
    );
  });

  context.restore();
};

// スケールバーのラベル文字列を生成する。テスト容易性のため export している。
export const formatScaleBarLabel = (meters: number): string => {
  if (meters >= 1000) {
    const km = meters / 1000;
    return `${Number.parseFloat(km.toFixed(2))} km`;
  }
  return `${Number.parseFloat(meters.toFixed(1))} m`;
};

/**
 * 印刷用 Canvas における「1 px が何メートルに相当するか」を計算する。
 *
 * - 縮尺指定あり: `1mm @ 1:N` は実世界で `N mm` (= `N/1000` m) なので、
 *   `1px = 1/pxPerMm mm = scaleDenominator / (pxPerMm * 1000)` メートル。
 * - 縮尺指定なし: 現在の地図ビュー上の実距離 (`totalMeters`) を、印刷出力上の
 *   地図領域幅 (`mapWidthPx`) で割って meters/px に変換する。
 *
 * 入力が 0 以下や有限でない場合は `null` を返し、呼び出し側でスケールバー描画を
 * スキップする。
 */
export const calcPrintMetersPerCanvasPixel = (
  input:
    | { scaleDenominator: number; pxPerMm: number }
    | { totalMeters: number; mapWidthPx: number }
): number | null => {
  if ("scaleDenominator" in input) {
    const { scaleDenominator, pxPerMm } = input;
    if (!Number.isFinite(scaleDenominator) || scaleDenominator <= 0) {
      return null;
    }
    if (!Number.isFinite(pxPerMm) || pxPerMm <= 0) {
      return null;
    }
    return scaleDenominator / (pxPerMm * 1000);
  }
  const { totalMeters, mapWidthPx } = input;
  if (!Number.isFinite(totalMeters) || totalMeters <= 0) return null;
  if (!Number.isFinite(mapWidthPx) || mapWidthPx <= 0) return null;
  return totalMeters / mapWidthPx;
};

// スケールバーの長さ候補（1/2/5/10 系列）から、引数以下の最大値を返す。テスト容易性のため export している。
export const getNiceDistance = (maxMeters: number): number => {
  if (maxMeters <= 0) return 1;
  const exponent = Math.floor(Math.log10(maxMeters));
  const base = 10 ** exponent;
  const candidates = [1, 2, 5, 10].map((value) => value * base);
  return (
    candidates.filter((candidate) => candidate <= maxMeters).at(-1) ?? base
  );
};

/**
 * 出力 Canvas の左下にスケールバーを描画する。
 *
 * Leaflet の `.leaflet-control-scale-line` (3 辺ボーダー / 半透明白背景 /
 * 内側にラベル) と同じ見た目を Canvas で再現する。距離は
 * `containerPointToLatLng` + `Map#distance` を用いて表示中の地図 CRS 上で
 * 計算するため、組み込み ScaleControl と同等の精度になる。
 */
export const drawScaleBarOnCanvas = (
  canvas: HTMLCanvasElement,
  mapInstance: Map,
  headerHeight = 0
): void => {
  const context = canvas.getContext("2d");
  if (!context) return;

  const mapSize = mapInstance.getSize();
  if (mapSize.x <= 0 || mapSize.y <= 0) return;

  // mapCanvas は dpr 倍で生成されているため、ここから dpr を逆算する。
  const dpr = canvas.width / mapSize.x;
  if (!Number.isFinite(dpr) || dpr <= 0) return;

  // Leaflet ScaleControl の既定 maxWidth=200 に揃える。
  const maxBarWidthCssPx = Math.min(200, Math.max(60, mapSize.x * 0.3));

  // 表示中の地図 CRS 上で実距離を求める。同じ緯度内ならメートル/ピクセル比は
  // ほぼ一定なので、ここで計算した値を左下のバー幅にそのまま適用する。
  const probeY = Math.max(0, Math.min(mapSize.y - 1, mapSize.y / 2));
  const probeFrom = mapInstance.containerPointToLatLng([0, probeY]);
  const probeTo = mapInstance.containerPointToLatLng([
    maxBarWidthCssPx,
    probeY,
  ]);
  const maxMeters = mapInstance.distance(probeFrom, probeTo);
  if (!Number.isFinite(maxMeters) || maxMeters <= 0) return;

  // 1, 2, 5, 10 系列のキリのよい距離に丸めて、その実距離分だけバー幅を縮める。
  const niceMeters = getNiceDistance(maxMeters);
  const barWidthCssPx = (niceMeters / maxMeters) * maxBarWidthCssPx;
  if (!Number.isFinite(barWidthCssPx) || barWidthCssPx <= 0) return;

  const label = formatScaleBarLabel(niceMeters);

  // Leaflet と同じ CSS パラメータを canvas px に換算する。
  // (border 2px / padding 2px 5px 1px / font-size 11px / box-sizing: border-box)
  const fontSize = 11 * dpr;
  const borderWidth = 2 * dpr;
  const padTop = 2 * dpr;
  const padBottom = 1 * dpr;
  const padX = 5 * dpr;
  const marginFromEdge = 10 * dpr;

  context.save();
  context.font = `${fontSize}px "Helvetica Neue", Arial, Helvetica, sans-serif`;
  context.textBaseline = "top";

  const labelWidth = context.measureText(label).width;
  // box-sizing: border-box 相当。バー幅はラベルが収まる最小幅でクリップする。
  const minWidth = labelWidth + padX * 2 + borderWidth * 2;
  const totalWidth = Math.max(barWidthCssPx * dpr, minWidth);
  const totalHeight = fontSize + padTop + padBottom + borderWidth;

  const mapBottom = headerHeight + mapSize.y * dpr;
  const x = marginFromEdge;
  const y = mapBottom - marginFromEdge - totalHeight;

  // 背景 (半透明白)。border が中心揃えで描かれるため、塗りは少し内側にする。
  context.fillStyle = "rgba(255, 255, 255, 0.5)";
  context.fillRect(x, y, totalWidth, totalHeight - borderWidth / 2);

  // ラベル
  context.fillStyle = "#333";
  context.textAlign = "left";
  context.fillText(label, x + borderWidth + padX, y + padTop);

  // border (左・下・右 の 3 辺。トップは描かない)
  context.strokeStyle = "#777";
  context.lineWidth = borderWidth;
  context.lineCap = "butt";
  context.lineJoin = "miter";
  const half = borderWidth / 2;
  context.beginPath();
  context.moveTo(x + half, y);
  context.lineTo(x + half, y + totalHeight - half);
  context.lineTo(x + totalWidth - half, y + totalHeight - half);
  context.lineTo(x + totalWidth - half, y);
  context.stroke();

  context.restore();
};

/**
 * 画像ダウンロード用に、地図キャプチャの上にタイトルバー、必要に応じて
 * スケールバーを合成する。
 */
export const composeImageCanvas = (
  mapCanvas: HTMLCanvasElement,
  title: string | undefined,
  showScaleBar: boolean | undefined,
  mapInstance: Map
): HTMLCanvasElement => {
  const titleText = title?.trim();
  const headerHeight = titleText ? Math.max(44, mapCanvas.height * 0.055) : 0;
  const outputCanvas = document.createElement("canvas");
  outputCanvas.width = mapCanvas.width;
  outputCanvas.height = mapCanvas.height + headerHeight;
  const context = outputCanvas.getContext("2d");
  if (!context) return mapCanvas;

  context.fillStyle = "#ffffff";
  context.fillRect(0, 0, outputCanvas.width, outputCanvas.height);
  if (titleText) {
    context.fillStyle = "#111827";
    context.font = `500 ${Math.max(16, headerHeight * 0.34)}px sans-serif`;
    context.textBaseline = "middle";
    context.fillText(titleText, 18, headerHeight / 2);
  }
  context.drawImage(mapCanvas, 0, headerHeight);

  if (showScaleBar) {
    drawScaleBarOnCanvas(outputCanvas, mapInstance, headerHeight);
  }

  const attributionText = getMapAttributionText(mapInstance);
  if (attributionText) {
    // 画像出力では Leaflet 画面の dpr に合わせてフォント等を拡大する。
    const mapSize = mapInstance.getSize();
    const dpr = mapSize.x > 0 ? mapCanvas.width / mapSize.x : 1;
    drawAttributionOnCanvas(outputCanvas, attributionText, {
      mapAreaXPx: 0,
      mapAreaYPx: headerHeight,
      mapAreaWidthPx: outputCanvas.width,
      mapAreaHeightPx: outputCanvas.height - headerHeight,
      fontSizePx: 11 * dpr,
      paddingXPx: 4 * dpr,
      paddingYPx: 2 * dpr,
      marginPx: 0,
    });
  }

  return outputCanvas;
};

/**
 * 印刷モードで縮尺を指定したときに、地図キャプチャを指定 LatLngBounds の領域へ
 * 切り出す。
 */
export const cropCanvasToBounds = (
  sourceCanvas: HTMLCanvasElement,
  mapInstance: Map,
  bounds: LatLngBounds
): HTMLCanvasElement => {
  const mapSize = mapInstance.getSize();
  const dpr = sourceCanvas.width / mapSize.x;
  const northWest = mapInstance.latLngToContainerPoint(bounds.getNorthWest());
  const southEast = mapInstance.latLngToContainerPoint(bounds.getSouthEast());
  const sx = Math.max(0, Math.min(northWest.x, southEast.x) * dpr);
  const sy = Math.max(0, Math.min(northWest.y, southEast.y) * dpr);
  const sw = Math.min(
    sourceCanvas.width - sx,
    Math.abs(southEast.x - northWest.x) * dpr
  );
  const sh = Math.min(
    sourceCanvas.height - sy,
    Math.abs(southEast.y - northWest.y) * dpr
  );
  const cropped = document.createElement("canvas");
  cropped.width = Math.max(1, Math.round(sw));
  cropped.height = Math.max(1, Math.round(sh));
  const context = cropped.getContext("2d");
  if (!context) return sourceCanvas;
  context.drawImage(
    sourceCanvas,
    sx,
    sy,
    sw,
    sh,
    0,
    0,
    cropped.width,
    cropped.height
  );
  return cropped;
};

/**
 * 印刷用 Canvas の地図領域内へ、左下にスケールバーを描画する。
 *
 * 画像出力の `drawScaleBarOnCanvas` と異なり、座標系の単位を「印刷の mm」で
 * 揃え直す。`metersPerCanvasPixel` は呼び出し側で算出する。
 */
const drawPrintScaleBarOnCanvas = (
  canvas: HTMLCanvasElement,
  metersPerCanvasPixel: number,
  mapAreaXPx: number,
  mapAreaYPx: number,
  mapAreaHeightPx: number,
  pxPerMm: number
): void => {
  const context = canvas.getContext("2d");
  if (!context) return;
  if (!Number.isFinite(metersPerCanvasPixel) || metersPerCanvasPixel <= 0) {
    return;
  }

  // 印刷物上で 40mm をスケールバー幅の上限とする。
  const maxBarWidthPx = 40 * pxPerMm;
  const maxMeters = maxBarWidthPx * metersPerCanvasPixel;
  if (!Number.isFinite(maxMeters) || maxMeters <= 0) return;

  const niceMeters = getNiceDistance(maxMeters);
  const barWidthPx = niceMeters / metersPerCanvasPixel;
  if (!Number.isFinite(barWidthPx) || barWidthPx <= 0) return;

  const label = formatScaleBarLabel(niceMeters);

  // 印刷物上で読みやすいサイズ感に揃える (mm 基準)
  const fontSize = 3 * pxPerMm;
  const borderWidth = 0.4 * pxPerMm;
  const padX = 1.5 * pxPerMm;
  const padTop = 0.6 * pxPerMm;
  const padBottom = 0.3 * pxPerMm;
  const marginFromEdge = 2 * pxPerMm;

  context.save();
  context.font = `${fontSize}px "Helvetica Neue", Arial, Helvetica, sans-serif`;
  context.textBaseline = "top";

  const labelWidth = context.measureText(label).width;
  const minWidth = labelWidth + padX * 2 + borderWidth * 2;
  const totalWidth = Math.max(barWidthPx, minWidth);
  const totalHeight = fontSize + padTop + padBottom + borderWidth;

  const x = mapAreaXPx + marginFromEdge;
  const y = mapAreaYPx + mapAreaHeightPx - marginFromEdge - totalHeight;

  // 背景 (半透明白)
  context.fillStyle = "rgba(255, 255, 255, 0.75)";
  context.fillRect(x, y, totalWidth, totalHeight - borderWidth / 2);

  // ラベル
  context.fillStyle = "#333";
  context.textAlign = "left";
  context.fillText(label, x + borderWidth + padX, y + padTop);

  // 左・下・右 の 3 辺
  context.strokeStyle = "#777";
  context.lineWidth = borderWidth;
  context.lineCap = "butt";
  context.lineJoin = "miter";
  const half = borderWidth / 2;
  context.beginPath();
  context.moveTo(x + half, y);
  context.lineTo(x + half, y + totalHeight - half);
  context.lineTo(x + totalWidth - half, y + totalHeight - half);
  context.lineTo(x + totalWidth - half, y);
  context.stroke();

  context.restore();
};

/**
 * 印刷用 (A4/A3) の Canvas を組み立てる。
 *
 * 出力サイズは入力地図 Canvas のサイズや縮尺に関係なく、用紙サイズから
 * 300dpi 相当のピクセル数を固定で決める。これにより printOptions だけで
 * 出力解像度が一意に定まる。
 */
export const composePrintCanvas = (
  mapCanvas: HTMLCanvasElement,
  title: string | undefined,
  printOptions: MapPrintOptions,
  showScaleBar: boolean,
  mapInstance: Map,
  dpi: number
): HTMLCanvasElement | null => {
  const layout = getPrintLayout(printOptions);
  const pxPerMm = getPxPerMm(dpi);

  const outputCanvas = document.createElement("canvas");
  outputCanvas.width = Math.round(layout.pageWidthMm * pxPerMm);
  outputCanvas.height = Math.round(layout.pageHeightMm * pxPerMm);
  const context = outputCanvas.getContext("2d");
  if (!context) return null;

  const mm = pxPerMm;
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = "high";
  context.fillStyle = "#ffffff";
  context.fillRect(0, 0, outputCanvas.width, outputCanvas.height);
  context.fillStyle = "#111827";
  context.font = `500 ${4 * mm}px sans-serif`;
  context.textBaseline = "middle";
  context.fillText(
    title?.trim() ?? "地図",
    layout.marginMm * mm,
    (layout.marginMm + layout.titleHeightMm / 2) * mm
  );

  const mapX = layout.mapXmm * mm;
  const mapY = layout.mapYmm * mm;
  const mapWidth = layout.mapWidthMm * mm;
  const mapHeight = layout.mapHeightMm * mm;
  context.drawImage(mapCanvas, mapX, mapY, mapWidth, mapHeight);
  context.strokeStyle = "#d1d5db";
  context.lineWidth = 1;
  context.strokeRect(mapX, mapY, mapWidth, mapHeight);

  if (showScaleBar) {
    let metersPerCanvasPixel: number | null = null;
    if (printOptions.scaleDenominator) {
      metersPerCanvasPixel = calcPrintMetersPerCanvasPixel({
        scaleDenominator: printOptions.scaleDenominator,
        pxPerMm,
      });
    } else {
      const mapSize = mapInstance.getSize();
      if (mapSize.x > 0 && mapSize.y > 0) {
        const probeY = Math.max(0, Math.min(mapSize.y - 1, mapSize.y / 2));
        const probeFrom = mapInstance.containerPointToLatLng([0, probeY]);
        const probeTo = mapInstance.containerPointToLatLng([mapSize.x, probeY]);
        const totalMeters = mapInstance.distance(probeFrom, probeTo);
        metersPerCanvasPixel = calcPrintMetersPerCanvasPixel({
          totalMeters,
          mapWidthPx: mapWidth,
        });
      }
    }
    if (metersPerCanvasPixel) {
      drawPrintScaleBarOnCanvas(
        outputCanvas,
        metersPerCanvasPixel,
        mapX,
        mapY,
        mapHeight,
        pxPerMm
      );
    }
  }

  const attributionText = getMapAttributionText(mapInstance);
  if (attributionText) {
    drawAttributionOnCanvas(outputCanvas, attributionText, {
      mapAreaXPx: mapX,
      mapAreaYPx: mapY,
      mapAreaWidthPx: mapWidth,
      mapAreaHeightPx: mapHeight,
      fontSizePx: 2.5 * mm,
      paddingXPx: 1 * mm,
      paddingYPx: 0.5 * mm,
      marginPx: 0,
    });
  }

  context.font = `${3.6 * mm}px sans-serif`;
  context.fillStyle = "#111827";
  context.fillText(
    printOptions.scaleDenominator
      ? `縮尺 1:${printOptions.scaleDenominator.toLocaleString("ja-JP")}`
      : "縮尺 指定なし",
    layout.marginMm * mm,
    (layout.pageHeightMm - layout.marginMm - layout.footerHeightMm / 2) * mm
  );
  return outputCanvas;
};
